import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, AlertCircle, CheckCircle, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const NoticeOfDisputePage = () => {
  const scrollToIntake = () => {
    window.location.href = '/#intake';
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Back Link */}
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        {/* Hero Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="max-w-4xl">
            <div className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-6 bg-[#d9fb06]/20 text-[#d9fb06]">
              Legal Documentation
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#d9fb06] mb-6 tracking-tight">
              What is a Notice of Dispute?
            </h1>
            <p className="text-lg md:text-xl text-[#888680] mb-8 leading-relaxed">
              A Notice of Dispute is a formal legal document that notifies a platform of your intent to challenge an enforcement action through proper legal channels.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content Column */}
            <div className="lg:col-span-2 space-y-8">
              {/* What It Is */}
              <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="w-6 h-6 text-[#d9fb06]" />
                  <h2 className="text-xl font-bold text-[#d9fb06]">Purpose of a Notice of Dispute</h2>
                </div>
                <div className="space-y-4 text-[#888680]">
                  <p>
                    A Notice of Dispute serves as formal documentation that you are challenging an enforcement action taken against your account. Unlike standard appeals submitted through platform support channels, a Notice of Dispute:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#d9fb06] mt-1 flex-shrink-0" />
                      <span>Is prepared for submission through official platform appeal channels</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#d9fb06] mt-1 flex-shrink-0" />
                      <span>Formally documents your dispute for potential legal proceedings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#d9fb06] mt-1 flex-shrink-0" />
                      <span>Requests specific procedural review of the enforcement action</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#d9fb06] mt-1 flex-shrink-0" />
                      <span>Preserves your rights for arbitration or litigation if needed</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* What It Contains */}
              <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Scale className="w-6 h-6 text-[#d9fb06]" />
                  <h2 className="text-xl font-bold text-[#d9fb06]">What a Notice of Dispute Contains</h2>
                </div>
                <div className="space-y-4 text-[#888680]">
                  <p>Our Notice of Dispute documents are comprehensive and include:</p>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    {[
                      { title: 'Account Identification', desc: 'Clear identification of the account(s) affected' },
                      { title: 'Enforcement Details', desc: 'Documentation of the specific action taken by the platform' },
                      { title: 'Policy Analysis', desc: 'Review of applicable terms of service and policies' },
                      { title: 'Procedural Objections', desc: 'Specific grounds for challenging the enforcement' },
                      { title: 'Evidence Summary', desc: 'Supporting documentation and evidence for your case' },
                      { title: 'Relief Requested', desc: 'Clear statement of the outcome you are seeking' }
                    ].map((item) => (
                      <div key={`contains-${item.title}`} className="bg-[#1a1c1b] p-4 rounded-lg">
                        <h3 className="text-[#d9fb06] font-medium mb-1">{item.title}</h3>
                        <p className="text-sm">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* How It Differs */}
              <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <AlertCircle className="w-6 h-6 text-[#d9fb06]" />
                  <h2 className="text-xl font-bold text-[#d9fb06]">Notice of Dispute vs. Standard Appeal</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[#888680] text-sm">
                    <thead>
                      <tr className="border-b border-[#3f4816]">
                        <th className="text-left py-3 pr-4 text-[#d9fb06]">Aspect</th>
                        <th className="text-left py-3 pr-4 text-[#d9fb06]">Standard Appeal</th>
                        <th className="text-left py-3 text-[#d9fb06]">Notice of Dispute</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-[#3f4816]/50">
                        <td className="py-3 pr-4 font-medium">Recipient</td>
                        <td className="py-3 pr-4">Customer Support</td>
                        <td className="py-3">Legal/Compliance Team</td>
                      </tr>
                      <tr className="border-b border-[#3f4816]/50">
                        <td className="py-3 pr-4 font-medium">Submission Method</td>
                        <td className="py-3 pr-4">Platform Form/Email</td>
                        <td className="py-3">Registered Agent / Certified Mail</td>
                      </tr>
                      <tr className="border-b border-[#3f4816]/50">
                        <td className="py-3 pr-4 font-medium">Documentation</td>
                        <td className="py-3 pr-4">Limited Text Field</td>
                        <td className="py-3">Comprehensive Legal Package</td>
                      </tr>
                      <tr className="border-b border-[#3f4816]/50">
                        <td className="py-3 pr-4 font-medium">Review Level</td>
                        <td className="py-3 pr-4">Automated/Tier 1</td>
                        <td className="py-3">Legal Review Required</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 font-medium">Legal Weight</td>
                        <td className="py-3 pr-4">None</td>
                        <td className="py-3">Formal Record Created</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Process Card */}
              <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-6">
                <h3 className="text-lg font-bold text-[#d9fb06] mb-4">Our Process</h3>
                <div className="space-y-4">
                  {[
                    { icon: FileText, step: '1', title: 'Case Review', desc: 'We review your situation and enforcement details' },
                    { icon: Scale, step: '2', title: 'Document Preparation', desc: 'Notice of Dispute drafted with supporting evidence' },
                    { icon: Send, step: '3', title: 'Formal Submission', desc: 'Sent via registered agent or certified mail' },
                    { icon: Clock, step: '4', title: 'Follow-Up', desc: 'We track response and advise on next steps' }
                  ].map((item) => (
                    <div key={`process-${item.step}`} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#d9fb06] flex items-center justify-center flex-shrink-0">
                        <span className="text-[#1a1c1b] font-bold text-sm">{item.step}</span>
                      </div>
                      <div>
                        <h4 className="text-[#d9fb06] font-medium text-sm">{item.title}</h4>
                        <p className="text-[#888680] text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Card */}
              <div className="bg-[#d9fb06] rounded-2xl p-6 text-center">
                <h3 className="text-lg font-bold text-[#1a1c1b] mb-2">Need a Notice of Dispute?</h3>
                <p className="text-[#1a1c1b]/80 text-sm mb-4">
                  Submit your case for review and we&apos;ll prepare your documentation.
                </p>
                <Button 
                  onClick={scrollToIntake}
                  className="bg-[#1a1c1b] text-[#d9fb06] hover:bg-[#1a1c1b]/90 rounded-full w-full font-semibold"
                  data-testid="notice-cta"
                >
                  Request Review
                </Button>
              </div>

              {/* Disclaimer */}
              <div className="bg-[#1a1c1b] border border-[#3f4816] rounded-xl p-4">
                <p className="text-[#888680] text-xs">
                  Social Dispute Co. is not a law firm and does not provide legal advice. We prepare documentation for formal submission. Platform decisions are made solely by the platform.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NoticeOfDisputePage;
